"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, ChevronRight, Download, FileText, Newspaper } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const newsItems = [{
  id: "news-1",
  title: "Housing Pressure Index (HPI) Reaches New Peak in Q1 2024",
  date: "2024-05-15",
  excerpt: "The latest AIB data reveals a significant surge in housing pressure across major metropolitan areas, driven by supply constraints.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/housing_pressure_index_report.png",
  link: "/MediaCentrePage"
}, {
  id: "news-2",
  title: "AIB Methodology Update: Integrating Real-Time Rental Data",
  date: "2024-05-10",
  excerpt: "Our research team has enhanced the Rental Exposure Index (REX) with high-frequency data points for greater accuracy.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/methodology_update_research.png",
  link: "/MethodologyPage"
}, {
  id: "news-3",
  title: "Financial Wellness Index Shows Resilience Amid Inflation",
  date: "2024-05-02",
  excerpt: "Despite rising costs, the FWI indicates a surprising level of household financial resilience in regional Australia.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/financial_wellness_analysis.png",
  link: "/MediaCentrePage"
}];
const circularItems = [{
  id: "circ-1",
  title: "AIB Technical Bulletin 08/2024: COLEX Calculation Revision",
  date: "2024-05-18",
  category: "Technical",
  link: "/MethodologyPage"
}, {
  id: "circ-2",
  title: "Media Release: Quarterly Economic Indicator Summary",
  date: "2024-05-12",
  category: "Media",
  link: "/MediaCentrePage"
}, {
  id: "circ-3",
  title: "AIB Governance: Independent Audit Report 2023-24",
  date: "2024-04-28",
  category: "Governance",
  link: "/AboutAIBPage"
}, {
  id: "circ-4",
  title: "Data Access Protocol for Academic Research Partnerships",
  date: "2024-04-15",
  category: "Partnerships",
  link: "/ContactPage"
}];
const getMonthShort = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short"
  }).toUpperCase();
};
const getDay = (dateString: string) => {
  const date = new Date(dateString);
  return date.getDate().toString().padStart(2, "0");
};
export default function LatestNewsCirculars() {
  return <section data-section-id="234611" id="press-insights" className="bg-background py-16 lg:py-24">
      <div className="container mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          
          {}
          <div className="flex flex-col">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-default text-2xl font-bold tracking-tight text-primary flex items-center gap-2">
                <Newspaper className="size-6 text-primary" />
                Press & Insights
              </h2>
            </div>
            <Separator className="mb-6 bg-border" />
            
            <div className="flex flex-col gap-6 flex-1">
              {newsItems.map((item, i) => <div key={item.id} data-index={i} className="group flex flex-col sm:flex-row gap-4 items-start">
                  <div className="shrink-0 overflow-hidden border-[0.5px] border-border w-full sm:w-32 h-48 sm:h-24 bg-muted">
                    <img src={item.image} alt={item.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 grayscale hover:grayscale-0" />
                  </div>
                  <div className="flex flex-col justify-center flex-1">
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
                      <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                      <time dateTime={item.date}>{item.date}</time>
                    </div>
                    <h3 className="font-default text-base font-semibold leading-tight text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">
                      <Link to={item.link}>{item.title}</Link>
                    </h3>
                    <p className="text-sm font-default text-muted-foreground line-clamp-2">
                      {item.excerpt}
                    </p>
                  </div>
                </div>)}
            </div>
            
            <div className="mt-8 pt-4 border-t border-border">
              <Button variant="outline" asChild className="w-full sm:w-auto group border-primary font-default uppercase tracking-wider text-xs hover:bg-primary hover:text-primary-foreground transition-all">
                <Link to="/MediaCentrePage">
                  View All Insights
                  <ChevronRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="flex flex-col">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-default text-2xl font-bold tracking-tight text-primary flex items-center gap-2">
                <FileText className="size-6 text-primary" />
                Technical Bulletins
              </h2>
            </div>
            <Separator className="mb-6 bg-border" />
            
            <div className="flex flex-col gap-3 flex-1">
              {circularItems.map((item, i) => <Card key={item.id} data-index={i} className="overflow-hidden border-[0.5px] border-border bg-card transition-all hover:border-primary/50 hover:bg-accent/5 py-0">
                  <CardContent className="p-3 flex items-center gap-4">
                    <div className="flex shrink-0 flex-col items-center justify-center bg-primary px-3 py-1.5 text-primary-foreground min-w-[64px]">
                      <span className="font-mono text-xl font-bold leading-none">{getDay(item.date)}</span>
                      <span className="font-mono text-[10px] font-medium tracking-wider">{getMonthShort(item.date)}</span>
                    </div>
                    
                    <div className="flex flex-1 flex-col justify-center min-w-0">
                      <span className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        {item.category}
                      </span>
                      <h3 className="font-default text-sm font-medium leading-snug text-foreground hover:text-primary transition-colors truncate whitespace-normal line-clamp-2">
                        <Link to={item.link}>{item.title}</Link>
                      </h3>
                    </div>
                    
                    <Button variant="ghost" size="icon" asChild className="shrink-0 text-muted-foreground hover:text-primary hover:bg-transparent">
                      <Link to={item.link} aria-label={`Download ${item.title}`}>
                        <Download className="size-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>)}
            </div>

            <div className="mt-8 pt-4 border-t border-border">
              <Button variant="outline" asChild className="w-full sm:w-auto group border-primary font-default uppercase tracking-wider text-xs hover:bg-primary hover:text-primary-foreground transition-all">
                <Link to="/MethodologyPage">
                  Methodology Archive
                  <ChevronRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}